import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDashboardSummary, type DashboardSummary } from '../features/dashboard/api';
import { useAuthStore } from '../features/auth/store';
import { usePinnedCustomers } from '../features/customer-pins/api';
import { useUnshippedSummary } from '../features/accounting/api';
import type { UnshippedSummaryResponse } from '@app/shared';

export function DashboardPage(): JSX.Element {
  const { data, isLoading, isError, error } = useDashboardSummary();
  const me = useAuthStore((s) => s.user);
  const pinnedQuery = usePinnedCustomers();
  const unshippedQuery = useUnshippedSummary();

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
      </div>
    );
  }

  const s = data?.data;
  if (!s) return <div className="text-slate-500">無資料</div>;


  return (
    <section className="grid gap-6">
      <header>
        <h1 className="text-2xl font-semibold">儀表板</h1>
        <p className="text-sm text-slate-500">嗨，{me?.displayName}。以下是你目前的工作概覽。</p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={me?.role === 'SALES' ? '我的客戶數' : '總客戶數'} value={s.myCustomerCount} />
        <StatCard label="近 7 天訪談" value={s.myInterviewCount7d} />
        <StatCard label="近 7 天附件上傳" value={s.attachmentCount7d} />
        <StatCard label="🔔 每日提醒中" value={s.notifyDailyCount} />
      </div>

      {/*
        尚未出貨(REQ-20260815008)。使用者要的是「一眼知道還有多少商品積著沒出」,
        所以數字直接放在標題下,清單就在同一區塊 —— 不必再點進另一個頁面。
        只算已過帳的單(草稿還沒定案、作廢 / 歷史單不該再出),見後端 unshippedSummary()。
      */}
      <section>
        <h2 className="text-lg font-semibold mb-2">📦 尚未出貨</h2>
        {unshippedQuery.isLoading ? (
          <div className="text-slate-500 text-sm">載入中…</div>
        ) : unshippedQuery.isError || !unshippedQuery.data ? (
          <div role="alert" className="text-red-600 text-sm">
            未出貨彙總載入失敗
          </div>
        ) : unshippedQuery.data.orderCount === 0 ? (
          <div className="text-slate-500 text-sm">
            目前沒有積壓未出貨的訂單,已過帳的單都已出貨完畢。
          </div>
        ) : (
          <UnshippedSection summary={unshippedQuery.data} />
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">📌 釘選客戶</h2>
        {pinnedQuery.isLoading ? (
          <div className="text-slate-500 text-sm">載入中…</div>
        ) : pinnedQuery.data && pinnedQuery.data.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {pinnedQuery.data.map((p) => (
              <Link
                key={p.id}
                to={`/customers/console?id=${p.id}`}
                className="bg-white shadow-sm rounded-md p-3 hover:shadow hover:bg-amber-50 transition"
                data-testid={`pinned-customer-${p.id}`}
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold truncate">{p.simpleName}</span>
                  <span className="text-slate-400 font-mono text-xs shrink-0">{p.cCode}</span>
                </div>
                <div className="text-xs text-slate-600 truncate mt-0.5">{p.fullName}</div>
                {p.mobile ? (
                  <div className="text-xs text-slate-500 mt-0.5">📱 {p.mobile}</div>
                ) : null}
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-slate-500 text-sm">
            目前沒有釘選客戶。到客戶操作台點擊工具列的「📌 釘選」可加入。
          </div>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">🔔 開啟每日提醒的訪談</h2>
        {s.notifyDailyInterviews.length === 0 ? (
          <div className="text-slate-500 text-sm">
            目前沒有訪談開啟每日提醒。到客戶訪談紀錄點「🔕 提醒」切換即可加入每日 AI 追蹤推播。
          </div>
        ) : (
          <NotifyDailyTable interviews={s.notifyDailyInterviews} />
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">最近訪談</h2>
        {s.recentInterviews.length === 0 ? (
          <div className="text-slate-500">尚無訪談紀錄</div>
        ) : (
          <table className="w-full bg-white shadow-sm rounded-md">
            <thead>
              <tr className="border-b text-left text-sm text-slate-600">
                <th className="px-4 py-2">客戶</th>
                <th className="px-4 py-2">類型</th>
                <th className="px-4 py-2">拜訪時間</th>
                <th className="px-4 py-2">建立者</th>
                <th className="px-4 py-2">摘要</th>
              </tr>
            </thead>
            <tbody>
              {s.recentInterviews.map((iv) => (
                <tr key={iv.id} className="border-b last:border-0 hover:bg-slate-50 text-sm">
                  <td className="px-4 py-2">
                    <Link
                      to={`/customers/${iv.customer.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {iv.customer.simpleName}
                    </Link>
                    <span className="ml-1 text-slate-400 font-mono text-xs">{iv.customer.cCode}</span>
                  </td>
                  <td className="px-4 py-2">{iv.interviewType?.name ?? '—'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {iv.visitedAt
                      ? new Date(iv.visitedAt).toLocaleDateString('zh-TW')
                      : new Date(iv.createdAt).toLocaleDateString('zh-TW')}
                  </td>
                  <td className="px-4 py-2">{iv.createdBy.displayName}</td>
                  <td className="px-4 py-2 text-slate-600 max-w-xs truncate">
                    {iv.notes ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </section>
  );
}

type UnshippedRow = UnshippedSummaryResponse['items'][number];
type UnshippedSortKey = 'orderNo' | 'txnDate' | 'customerSimpleName';

/** 未出貨清單預設列幾筆(維持原本的區塊高度),其餘按「顯示全部」才展開 */
const UNSHIPPED_VISIBLE_LIMIT = 50;

/**
 * 未出貨彙總(REQ-20260815008):數字 + 簡單清單。
 *
 * 單號點進去直接開那張單的**出貨登記彈窗**(REQ-20260815009)。原本連的是編輯表單
 * (理由是「出貨登記在出貨管理列表的『出貨』按鈕」),但這個區塊列的就是還沒出完的單,
 * 使用者從這裡點下去要做的事只有一件 —— 登記出貨,不該再自己切過去找一次。
 * 彈窗仍是「出貨管理」那一份(`ShipmentModal`),這裡沒有重複做輸入介面。
 *
 * 單號 / 日期 / 客戶 可點欄位排序;資料已整包載入,純前端排序。預設 txnDate 升冪
 * = 後端給的順序(最舊的排前面)。
 *
 * 後端 REQ-20260829001 起回傳**全部**未出貨的單(原本截斷成 50 筆,新開的單排在
 * 最後就被擋在畫面外)。畫面預設仍只列 50 列維持原本的高度,要看全部按按鈕展開 ——
 * 展開狀態是純前端 state,不打 API、不進網址(做法比照 REQ-20260827001 的「全部紀錄」)。
 */
function UnshippedSection({ summary }: { summary: UnshippedSummaryResponse }): JSX.Element {
  const [sortKey, setSortKey] = useState<UnshippedSortKey>('txnDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...summary.items].sort((a, b) => dir * compareUnshippedRow(a, b, sortKey));
  }, [summary.items, sortKey, sortDir]);

  // 展開前後排序都作用在整包資料上,收合時取的是排序後的前 50 筆
  const visible = showAll ? sorted : sorted.slice(0, UNSHIPPED_VISIBLE_LIMIT);

  const onSort = (key: UnshippedSortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="grid gap-2">
      <p className="text-sm text-slate-700" data-testid="unshipped-summary-line">
        目前有 <span className="font-semibold">{summary.orderCount}</span> 筆訂單、
        <span className="font-semibold">{summary.lineCount}</span> 項商品尚未出貨完畢(未出貨數量合計{' '}
        <span className="font-mono">{summary.unshippedQty}</span>)。
      </p>
      <table className="w-full bg-white shadow-sm rounded-md">
        <thead>
          <tr className="border-b text-left text-sm text-slate-600">
            <SortableTh label="單號" k="orderNo" active={sortKey} dir={sortDir} onSort={onSort} />
            <SortableTh label="日期" k="txnDate" active={sortKey} dir={sortDir} onSort={onSort} />
            <SortableTh
              label="客戶"
              k="customerSimpleName"
              active={sortKey}
              dir={sortDir}
              onSort={onSort}
            />
            <th className="px-4 py-2">商品名稱</th>
            <th className="px-4 py-2 text-right">未出貨數量</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((o) => (
            <tr key={o.orderId} className="border-b last:border-0 hover:bg-amber-50 text-sm">
              <td className="px-4 py-2 whitespace-nowrap font-mono">
                <Link
                  to={`/accounting?tab=sales&shipOrderId=${o.orderId}&orderNo=${encodeURIComponent(o.orderNo)}`}
                  className="text-blue-600 hover:underline"
                  data-testid={`unshipped-order-${o.orderId}`}
                >
                  {o.orderNo}
                </Link>
              </td>
              <td className="px-4 py-2 whitespace-nowrap">{o.txnDate}</td>
              <td className="px-4 py-2 max-w-xs truncate">
                <Link
                  to={`/customers/console?id=${o.customerId}`}
                  className="text-blue-600 hover:underline"
                  data-testid={`unshipped-customer-${o.orderId}`}
                >
                  {o.customerSimpleName}
                </Link>
              </td>
              {/* 品名可能很長,截斷顯示、完整內容放 title 給 hover 看(同上面的客戶欄) */}
              <td className="px-4 py-2 max-w-xs truncate" title={o.productNames.join('、')}>
                {o.productNames.join('、')}
              </td>
              <td className="px-4 py-2 text-right font-mono">{o.unshippedQty}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* 預設只列 50 列,超過的用按鈕展開 / 收合(REQ-20260829001) */}
      {sorted.length > UNSHIPPED_VISIBLE_LIMIT ? (
        <div>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-xs border rounded px-2 py-1 text-slate-700 hover:bg-slate-100"
            data-testid="unshipped-toggle-all"
          >
            {showAll
              ? `只顯示最舊的 ${UNSHIPPED_VISIBLE_LIMIT} 筆`
              : `顯示全部 ${sorted.length} 筆`}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function compareUnshippedRow(a: UnshippedRow, b: UnshippedRow, key: UnshippedSortKey): number {
  // txnDate 是 YYYY-MM-DD 字串,字典序 = 日期序
  return a[key].localeCompare(b[key], 'zh-Hant');
}

function StatCard({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="bg-white shadow-sm rounded-md p-4">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="text-3xl font-semibold mt-1">{value}</div>
    </div>
  );
}

type NotifyRow = DashboardSummary['notifyDailyInterviews'][number];
type NotifySortKey = 'customer' | 'phone' | 'type' | 'createdAt';

/** 每日提醒訪談表（客戶 / 電話 / 類型 / 建立時間 可點欄位排序；資料已全載入，純前端排序） */
function NotifyDailyTable({ interviews }: { interviews: NotifyRow[] }): JSX.Element {
  const [sortKey, setSortKey] = useState<NotifySortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...interviews].sort((a, b) => dir * compareNotifyRow(a, b, sortKey));
  }, [interviews, sortKey, sortDir]);

  const onSort = (key: NotifySortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <table className="w-full bg-white shadow-sm rounded-md">
      <thead>
        <tr className="border-b text-left text-sm text-slate-600">
          <SortableTh label="客戶" k="customer" active={sortKey} dir={sortDir} onSort={onSort} />
          <SortableTh label="電話" k="phone" active={sortKey} dir={sortDir} onSort={onSort} />
          <SortableTh label="類型" k="type" active={sortKey} dir={sortDir} onSort={onSort} />
          <SortableTh label="建立時間" k="createdAt" active={sortKey} dir={sortDir} onSort={onSort} />
          <th className="px-4 py-2">建立者</th>
          <th className="px-4 py-2">摘要</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((iv) => (
          <tr key={iv.id} className="border-b last:border-0 hover:bg-amber-50 text-sm">
            <td className="px-4 py-2">
              <Link
                to={`/customers/console?id=${iv.customer.id}&interviewId=${iv.id}`}
                className="text-blue-600 hover:underline"
              >
                {iv.customer.simpleName}
              </Link>
              <span className="ml-1 text-slate-400 font-mono text-xs">{iv.customer.cCode}</span>
            </td>
            <td className="px-4 py-2 text-slate-600">{iv.customer.phone ?? '—'}</td>
            <td className="px-4 py-2">{iv.interviewType?.name ?? '—'}</td>
            <td className="px-4 py-2 whitespace-nowrap">
              {new Date(iv.createdAt).toLocaleDateString('zh-TW')}
            </td>
            <td className="px-4 py-2">{iv.createdBy.displayName}</td>
            <td className="px-4 py-2 text-slate-600 max-w-xs truncate">{iv.notes ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** 排序 key 的型別由使用端決定(本檔有兩張表各自的 key),行為與 SalesOrdersPanel 相同 */
function SortableTh<K extends string>({
  label,
  k,
  active,
  dir,
  onSort,
}: {
  label: string;
  k: K;
  active: K;
  dir: 'asc' | 'desc';
  onSort: (k: K) => void;
}): JSX.Element {
  const isActive = active === k;
  return (
    <th className="px-4 py-2">
      <button
        type="button"
        onClick={() => onSort(k)}
        className="inline-flex items-center gap-1 hover:text-blue-600"
        aria-label={`依${label}排序`}
      >
        {label}
        <span className={isActive ? 'text-blue-600 text-xs' : 'text-slate-300 text-xs'}>
          {isActive && dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}

function compareNotifyRow(a: NotifyRow, b: NotifyRow, key: NotifySortKey): number {
  switch (key) {
    case 'customer':
      return a.customer.simpleName.localeCompare(b.customer.simpleName, 'zh-Hant');
    case 'phone':
      return (a.customer.phone ?? '').localeCompare(b.customer.phone ?? '');
    case 'type':
      return (a.interviewType?.name ?? '').localeCompare(b.interviewType?.name ?? '', 'zh-Hant');
    case 'createdAt':
      // ISO 字串字典序 = 時間序
      return a.createdAt.localeCompare(b.createdAt);
    default:
      return 0;
  }
}
